import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCog, Plus, Trash2, Loader2, CreditCard, Users, Clock, Coins } from "lucide-react";
import { format } from "date-fns";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const DURATION_OPTIONS = [24, 48, 72, 168];

function DurationPicker({ value, onChange, testid }) {
  const toggle = (d) => onChange(value.includes(d) ? value.filter((x) => x !== d) : [...value, d].sort((a, b) => a - b));
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {DURATION_OPTIONS.map((d) => (
        <button key={d} type="button" onClick={() => toggle(d)} data-testid={`${testid}-${d}`}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors duration-200 ${value.includes(d) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
          {d >= 168 ? `${d / 24}d` : `${d}h`}
        </button>
      ))}
    </div>
  );
}

export default function Resellers() {
  const [resellers, setResellers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", credits: 0, allowed_test_durations: [24] });
  const [saving, setSaving] = useState(false);
  const [creditFor, setCreditFor] = useState(null);
  const [creditAmt, setCreditAmt] = useState("");
  const [crediting, setCrediting] = useState(false);
  const [durFor, setDurFor] = useState(null);
  const [durVal, setDurVal] = useState([]);
  const [savingDur, setSavingDur] = useState(false);
  const [minFor, setMinFor] = useState(null);
  const [minVal, setMinVal] = useState("");
  const [savingMin, setSavingMin] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/resellers"); setResellers(data); }
    catch { toast.error("Failed to load resellers"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/resellers", { ...form, credits: Number(form.credits) });
      toast.success("Reseller created");
      setOpen(false); setForm({ name: "", email: "", password: "", credits: 0, allowed_test_durations: [24] });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const saveDurations = async () => {
    setSavingDur(true);
    try {
      await api.put(`/resellers/${durFor.id}/durations`, { allowed_test_durations: durVal });
      toast.success("Durations updated");
      setDurFor(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSavingDur(false); }
  };

  const addCredits = async () => {
    setCrediting(true);
    try {
      await api.post("/resellers/credits", { reseller_id: creditFor.id, amount: Number(creditAmt) });
      toast.success("Credits added");
      setCreditFor(null); setCreditAmt("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setCrediting(false); }
  };

  const saveMinCredits = async () => {
    setSavingMin(true);
    try {
      const val = minVal === "" ? null : Number(minVal);
      await api.put(`/resellers/${minFor.id}/min-test-credits`, { min_test_credits: val });
      toast.success("Minimum test credits updated");
      setMinFor(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSavingMin(false); }
  };

  const remove = async (r) => {
    try { await api.delete(`/resellers/${r.id}`); toast.success("Reseller removed"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="resellers-page">
      <PageHeader overline="Network" title="Reseller System" description="Manage resellers and their credit balances.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-reseller-button"><Plus className="h-4 w-4 mr-2" /> Add Reseller</Button>
          </DialogTrigger>
          <DialogContent className="bg-popover sm:max-w-md">
            <DialogHeader><DialogTitle className="font-display">New Reseller</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="reseller-name" /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="reseller-email" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="reseller-password" /></div>
                <div className="space-y-2"><Label>Initial credits</Label><Input type="number" value={form.credits} onChange={(e) => setForm((f) => ({ ...f, credits: e.target.value }))} data-testid="reseller-credits" /></div>
              </div>
              <div className="space-y-2">
                <Label>Allowed test durations</Label>
                <DurationPicker value={form.allowed_test_durations} onChange={(v) => setForm((f) => ({ ...f, allowed_test_durations: v }))} testid="reseller-durations" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving || !form.name || !form.email || !form.password} data-testid="reseller-save">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Card className="border-border overflow-hidden">
        {!resellers ? (
          <div className="p-4 space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : resellers.length === 0 ? (
          <div className="p-12 text-center">
            <UserCog className="h-10 w-10 mx-auto text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">No resellers yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Reseller</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead className="hidden md:table-cell">Test Durations</TableHead>
                  <TableHead className="hidden sm:table-cell">Customers</TableHead>
                  <TableHead className="hidden sm:table-cell">Sales</TableHead>
                  <TableHead className="hidden md:table-cell">Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {resellers.map((r) => (
                  <TableRow key={r.id} data-testid={`reseller-row-${r.id}`}>
                    <TableCell>
                      <p className="font-medium">{r.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{r.email}</p>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="border-accent/40 text-accent font-mono">{Number(r.credits).toLocaleString()} cr</Badge></TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(r.allowed_test_durations || []).length === 0 ? <span className="text-xs text-muted-foreground">none</span> :
                          r.allowed_test_durations.map((d) => <Badge key={d} variant="secondary" className="text-[10px] font-mono">{d >= 168 ? `${d / 24}d` : `${d}h`}</Badge>)}
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell"><span className="flex items-center gap-1.5 text-sm text-muted-foreground"><Users className="h-3.5 w-3.5" /> {r.customer_count}</span></TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{r.total_sales}</TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-muted-foreground font-mono">{r.created_at ? format(new Date(r.created_at), "dd MMM yyyy") : "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" className="text-accent" onClick={() => { setCreditFor(r); setCreditAmt(""); }} data-testid={`add-credits-${r.id}`}><CreditCard className="h-4 w-4 mr-1.5" /> Credits</Button>
                        <Button variant="ghost" size="sm" onClick={() => { setDurFor(r); setDurVal(r.allowed_test_durations || []); }} data-testid={`edit-durations-${r.id}`}><Clock className="h-4 w-4 mr-1.5" /> Durations</Button>
                        <Button variant="ghost" size="sm" onClick={() => { setMinFor(r); setMinVal(r.min_test_credits ?? ""); }} data-testid={`edit-mincredits-${r.id}`}><Coins className="h-4 w-4 mr-1.5" /> Min test</Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`reseller-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-popover">
                            <AlertDialogHeader><AlertDialogTitle>Remove {r.name}?</AlertDialogTitle>
                              <AlertDialogDescription>Their customers will remain in the system.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(r)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Remove</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={!!creditFor} onOpenChange={(o) => !o && setCreditFor(null)}>
        <DialogContent className="bg-popover sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Add credits</DialogTitle></DialogHeader>
          {creditFor && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">Current balance for <span className="text-foreground font-medium">{creditFor.name}</span>: <span className="font-mono text-accent">{Number(creditFor.credits).toLocaleString()} cr</span></p>
              <div className="space-y-2"><Label>Credits to add</Label><Input type="number" value={creditAmt} onChange={(e) => setCreditAmt(e.target.value)} placeholder="10" data-testid="credit-amount" /></div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={addCredits} disabled={crediting || !creditAmt} data-testid="credit-confirm">
              {crediting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Add credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!durFor} onOpenChange={(o) => !o && setDurFor(null)}>
        <DialogContent className="bg-popover sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Allowed test durations</DialogTitle></DialogHeader>
          {durFor && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">Choose which test durations <span className="text-foreground font-medium">{durFor.name}</span> may offer. Panel limits still apply.</p>
              <DurationPicker value={durVal} onChange={setDurVal} testid="edit-duration-picker" />
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveDurations} disabled={savingDur} data-testid="save-durations">
              {savingDur && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!minFor} onOpenChange={(o) => !o && setMinFor(null)}>
        <DialogContent className="bg-popover sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Minimum test credits</DialogTitle></DialogHeader>
          {minFor && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground"><span className="text-foreground font-medium">{minFor.name}</span> must hold at least this many credits to create a test. Leave empty to use the global default.</p>
              <div className="space-y-2"><Label>Minimum credits (empty = global default)</Label><Input type="number" min="0" value={minVal} onChange={(e) => setMinVal(e.target.value)} placeholder="global default" data-testid="mincredits-input" /></div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveMinCredits} disabled={savingMin} data-testid="save-mincredits">
              {savingMin && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
